import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { Box, useTheme, useMediaQuery, IconButton } from "@mui/material";
import { ChevronLeft } from "react-bootstrap-icons";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { UserDetailsProvider } from "../../contexts/UserDetailsContext";
import { HeaderToolbarProvider } from "../../contexts/HeaderToolbarContext";

const SIDEBAR_WIDTH = 270;
const COLLAPSED_WIDTH = 80;
const HEADER_HEIGHT = 64;

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  /** Toggle sidebar open/close */
  const handleSidebarToggle = () => setIsSidebarOpen((prev) => !prev);

  /** Toggle sidebar expand/collapse */
  const handleSidebarCollapse = () => setIsSidebarCollapsed((prev) => !prev);

  return (
    <UserDetailsProvider>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        {/* Sidebar */}
        <Sidebar
          open={isSidebarOpen}
          onToggle={handleSidebarToggle}
          collapsed={isSidebarCollapsed}
          onCollapse={handleSidebarCollapse}
        />

        {/* Floating Collapse Button (only desktop) */}
        {!isMobile && isSidebarOpen && (
          <Box
            sx={{
              position: "fixed",
              top: `${HEADER_HEIGHT}px`,
              left: isSidebarCollapsed ? `${COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px`,
              zIndex: theme.zIndex.drawer + 2,
              transition: theme.transitions.create("left", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            <IconButton
              onClick={handleSidebarCollapse}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "white",
                border: "2px solid #dee2e6",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                transform: "translate(-50%, -50%)",
                "&:hover": {
                  backgroundColor: "rgba(22, 67, 135, 0.08)",
                  border: "2px solid rgba(22, 67, 135, 0.3)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.3)",
                },
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "rgba(22, 67, 135, 0.8)",
                  transform: isSidebarCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <ChevronLeft size={16} />
              </Box>
            </IconButton>
          </Box>
        )}

        {/* Main Content */}
        <HeaderToolbarProvider>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              marginLeft: isMobile
                ? 0
                : isSidebarOpen
                  ? isSidebarCollapsed
                    ? `${COLLAPSED_WIDTH}px`
                    : 0
                  : `-${SIDEBAR_WIDTH + 10}px`,
              transition: theme.transitions.create("margin", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            }}
          >
            {/* Header */}
            <Header onMenuClick={handleSidebarToggle} />

            {/* Page Content */}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                padding: 3,
                marginTop: `${HEADER_HEIGHT}px`,
                minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                backgroundColor: theme.palette.background.default,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Outlet />
              </Box>
              <Footer />
            </Box>
          </Box>
        </HeaderToolbarProvider>
      </Box>
    </UserDetailsProvider>
  );
};

export default MainLayout;
